import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useData } from '../context/DataContext';
import VehicleCard from '../components/VehicleCard';

const { FiSearch, FiFilter, FiGrid, FiList, FiX } = FiIcons;

const CatalogPage = () => {
  const { vehicles } = useData();
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    brand: '',
    minPrice: '',
    maxPrice: '',
    minYear: '',
    maxYear: '',
    minKm: '',
    maxKm: '',
    fuel: '',
    transmission: '',
    type: '',
    color: ''
  });
  const [sortBy, setSortBy] = useState('newest');

  const brands = [...new Set(vehicles.map(v => v.brand))];
  const fuelTypes = [...new Set(vehicles.map(v => v.fuel))];
  const transmissionTypes = [...new Set(vehicles.map(v => v.transmission))];
  const vehicleTypes = [...new Set(vehicles.map(v => v.type))];
  const colors = [...new Set(vehicles.map(v => v.color))];

  const filteredAndSortedVehicles = useMemo(() => {
    let filtered = vehicles.filter(vehicle => {
      if (vehicle.status !== 'active') return false;
      // Search term filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const vehicleText = `${vehicle.brand} ${vehicle.model} ${vehicle.version}`.toLowerCase();
        if (!vehicleText.includes(search)) return false;
      }
      // Brand filter
      if (filters.brand && vehicle.brand !== filters.brand) return false;
      // Price filters
      if (filters.minPrice && vehicle.price < parseInt(filters.minPrice)) return false;
      if (filters.maxPrice && vehicle.price > parseInt(filters.maxPrice)) return false;
      // Year filters
      if (filters.minYear && vehicle.year < parseInt(filters.minYear)) return false;
      if (filters.maxYear && vehicle.year > parseInt(filters.maxYear)) return false;
      // Kilometers filters
      if (filters.minKm && vehicle.kilometers < parseInt(filters.minKm)) return false;
      if (filters.maxKm && vehicle.kilometers > parseInt(filters.maxKm)) return false;
      // Other filters
      if (filters.fuel && vehicle.fuel !== filters.fuel) return false;
      if (filters.transmission && vehicle.transmission !== filters.transmission) return false;
      if (filters.type && vehicle.type !== filters.type) return false;
      if (filters.color && vehicle.color !== filters.color) return false;
      return true;
    });

    // Sort vehicles
    switch (sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'year-desc':
        filtered.sort((a, b) => b.year - a.year);
        break;
      case 'year-asc':
        filtered.sort((a, b) => a.year - b.year);
        break;
      case 'km-asc':
        filtered.sort((a, b) => a.kilometers - b.kilometers);
        break;
      case 'km-desc':
        filtered.sort((a, b) => b.kilometers - a.kilometers);
        break;
      default: // newest
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    return filtered;
  }, [vehicles, searchTerm, filters, sortBy]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      brand: '',
      minPrice: '',
      maxPrice: '',
      minYear: '',
      maxYear: '',
      minKm: '',
      maxKm: '',
      fuel: '',
      transmission: '',
      type: '',
      color: ''
    });
    setSearchTerm('');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-8 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#2c3e50] mb-4">Catálogo de Vehículos</h1>
          <p className="text-xl text-gray-600">
            Descubre nuestra amplia selección de vehículos de segunda mano
          </p>
        </div>

        {/* Search and Controls */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por marca, modelo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
              />
            </div>
            {/* Controls */}
            <div className="flex items-center gap-4">
              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
              >
                <option value="newest">Más Recientes</option>
                <option value="price-asc">Precio: Menor a Mayor</option>
                <option value="price-desc">Precio: Mayor a Menor</option>
                <option value="year-desc">Año: Más Nuevo</option>
                <option value="year-asc">Año: Más Antiguo</option>
                <option value="km-asc">Km: Menos a Más</option>
                <option value="km-desc">Km: Más a Menos</option>
              </select>
              {/* View Mode */}
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-3 ${viewMode === 'grid' ? 'bg-[#005c91] text-white' : 'bg-white text-gray-600'}`}
                >
                  <SafeIcon icon={FiGrid} className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-3 ${viewMode === 'list' ? 'bg-[#005c91] text-white' : 'bg-white text-gray-600'}`}
                >
                  <SafeIcon icon={FiList} className="w-5 h-5" />
                </button>
              </div>
              {/* Filters Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="bg-[#005c91] hover:bg-[#3498db] text-white px-4 py-3 rounded-lg transition-colors flex items-center gap-2"
              >
                <SafeIcon icon={FiFilter} className="w-5 h-5" />
                <span className="hidden sm:inline">Filtros</span>
              </button>
            </div>
          </div>
          {/* Results count */}
          <div className="mt-4 text-gray-600">
            Mostrando {filteredAndSortedVehicles.length} de {vehicles.filter(v => v.status === 'active').length} vehículos
          </div>
        </div>

        <div className="flex gap-8">
          {/* Filters Sidebar */}
          <motion.div
            initial={false}
            animate={{ width: showFilters ? 320 : 0, opacity: showFilters ? 1 : 0 }}
            className="overflow-hidden"
          >
            <div className="bg-white rounded-lg shadow-md p-6 w-80">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-[#2c3e50]">Filtros</h3>
                <button
                  onClick={clearFilters}
                  className="text-[#e67e22] hover:text-orange-600 font-medium flex items-center gap-1"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                  Limpiar
                </button>
              </div>
              <div className="space-y-6">
                {/* Brand */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Marca</label>
                  <select
                    value={filters.brand}
                    onChange={(e) => handleFilterChange('brand', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                  >
                    <option value="">Todas las marcas</option>
                    {brands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                </div>
                {/* Price Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Precio (€)</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Mínimo"
                      value={filters.minPrice}
                      onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                    />
                    <input
                      type="number"
                      placeholder="Máximo"
                      value={filters.maxPrice}
                      onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                    />
                  </div>
                </div>
                {/* Year Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Año</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Desde"
                      value={filters.minYear}
                      onChange={(e) => handleFilterChange('minYear', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                    />
                    <input
                      type="number"
                      placeholder="Hasta"
                      value={filters.maxYear}
                      onChange={(e) => handleFilterChange('maxYear', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                    />
                  </div>
                </div>
                {/* Kilometers Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kilómetros</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Mínimo"
                      value={filters.minKm}
                      onChange={(e) => handleFilterChange('minKm', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                    />
                    <input
                      type="number"
                      placeholder="Máximo"
                      value={filters.maxKm}
                      onChange={(e) => handleFilterChange('maxKm', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                    />
                  </div>
                </div>
                {/* Fuel Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Combustible</label>
                  <select
                    value={filters.fuel}
                    onChange={(e) => handleFilterChange('fuel', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                  >
                    <option value="">Todos</option>
                    {fuelTypes.map(fuel => (
                      <option key={fuel} value={fuel}>{fuel}</option>
                    ))}
                  </select>
                </div>
                {/* Transmission */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Transmisión</label>
                  <select
                    value={filters.transmission}
                    onChange={(e) => handleFilterChange('transmission', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                  >
                    <option value="">Todas</option>
                    {transmissionTypes.map(transmission => (
                      <option key={transmission} value={transmission}>{transmission}</option>
                    ))}
                  </select>
                </div>
                {/* Vehicle Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
                  <select
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                  >
                    <option value="">Todos</option>
                    {vehicleTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                {/* Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                  <select
                    value={filters.color}
                    onChange={(e) => handleFilterChange('color', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                  >
                    <option value="">Todos</option>
                    {colors.map(color => (
                      <option key={color} value={color}>{color}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Vehicles Grid/List */}
          <div className="flex-1">
            {filteredAndSortedVehicles.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <div className="text-gray-400 mb-4">
                  <SafeIcon icon={FiSearch} className="w-16 h-16 mx-auto" />
                </div>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No se encontraron vehículos</h3>
                <p className="text-gray-500">Intenta ajustar tus filtros de búsqueda</p>
                <button
                  onClick={clearFilters}
                  className="mt-4 bg-[#005c91] hover:bg-[#3498db] text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Limpiar Filtros
                </button>
              </div>
            ) : (
              <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-6'}>
                {filteredAndSortedVehicles.map((vehicle, index) => (
                  <motion.div
                    key={vehicle.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    {viewMode === 'grid' ? (
                      <VehicleCard vehicle={vehicle} />
                    ) : (
                      <VehicleListItem vehicle={vehicle} />
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const VehicleListItem = ({ vehicle }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(price);
  };
  const formatKilometers = (km) => {
    return new Intl.NumberFormat('es-ES').format(km) + ' km';
  };
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
      <div className="flex">
        <div className="w-64 h-48 flex-shrink-0">
          <img
            src={vehicle.images[0] || 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400&h=250&fit=crop'}
            alt={`${vehicle.brand} ${vehicle.model}`}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-xl font-bold text-[#2c3e50] mb-1">
                {vehicle.brand} {vehicle.model}
              </h3>
              <p className="text-gray-600">{vehicle.version}</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-[#27ae60] mb-1">
                {formatPrice(vehicle.price)}
              </div>
              <div className="text-sm text-gray-500">{vehicle.year}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="text-center">
              <div className="text-sm text-gray-500">Kilómetros</div>
              <div className="font-semibold">{formatKilometers(vehicle.kilometers)}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-500">Combustible</div>
              <div className="font-semibold">{vehicle.fuel}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-500">Transmisión</div>
              <div className="font-semibold">{vehicle.transmission}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-500">Tipo</div>
              <div className="font-semibold">{vehicle.type}</div>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {vehicle.description?.substring(0, 100)}...
            </div>
            <Link
              to={`/vehiculo/${vehicle.id}`}
              className="bg-[#005c91] hover:bg-[#3498db] text-white px-6 py-2 rounded-lg font-semibold transition-colors"
            >
              Ver Detalles
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogPage;